import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { detectBrowserLocale, isLocale, type Locale } from '@/i18n';
import { readString, writeString } from '@/lib/storage';

export type ThemePreference = 'light' | 'dark' | 'system';

function applyTheme(theme: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.dataset.theme = theme;
}

export const usePrefsStore = defineStore('prefs', () => {
  const theme = ref<ThemePreference>('system');
  const locale = ref<Locale>('pt-BR');
  const sound = ref(false);
  const hydrated = ref(false);

  function hydrate(routeLocale: Locale | null): void {
    const storedTheme = readString('theme');
    theme.value = storedTheme === 'light' || storedTheme === 'dark' ? storedTheme : 'system';
    const storedLocale = readString('locale');
    locale.value = routeLocale ?? (isLocale(storedLocale) ? storedLocale : detectBrowserLocale());
    sound.value = readString('sound') === 'on';
    hydrated.value = true;
    if (typeof window !== 'undefined') {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (theme.value === 'system') applyTheme('system');
      });
    }
  }

  function setTheme(value: ThemePreference): void {
    theme.value = value;
    writeString('theme', value);
    applyTheme(value);
  }

  function cycleTheme(): void {
    const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    setTheme(isDark ? 'light' : 'dark');
  }

  function setLocale(value: Locale): void {
    locale.value = value;
    writeString('locale', value);
  }

  function setSound(value: boolean): void {
    sound.value = value;
    writeString('sound', value ? 'on' : 'off');
  }

  watch(locale, (value) => {
    if (typeof document !== 'undefined') document.documentElement.lang = value;
  });

  return { theme, locale, sound, hydrated, hydrate, setTheme, cycleTheme, setLocale, setSound };
});
